import { useState } from 'react'
import {
  Label,
  Button,
  Input,
  Checkbox,
} from '@creanizer/ui'
import { ChevronDown, ChevronUp, Search, Eye, Users, Shield, Package, Globe, Map } from 'lucide-react'

interface EntitySelectorProps {
  title: string
  icon: 'users' | 'shield' | 'package' | 'globe' | 'map'
  entities: Array<{ id: string; name: string; summary?: string; type?: string }>
  selectedIds: string[]
  onToggle: (id: string) => void
  onViewDetail?: (entity: any) => void
  searchPlaceholder?: string
}

export function EntitySelector({
  title,
  icon,
  entities,
  selectedIds,
  onToggle,
  onViewDetail,
  searchPlaceholder = '名前で検索...'
}: EntitySelectorProps) {
  const [showSelector, setShowSelector] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const IconComponent = icon === 'users' ? Users : icon === 'shield' ? Shield : icon === 'package' ? Package : icon === 'globe' ? Globe : Map
  const unitName = icon === 'users' ? '人' : icon === 'shield' ? '体' : icon === 'package' ? '個' : icon === 'globe' ? '件' : '箇所'

  const filteredEntities = entities.filter(entity => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      entity.name.toLowerCase().includes(query) ||
      entity.summary?.toLowerCase().includes(query) ||
      entity.type?.toLowerCase().includes(query)
    )
  })

  if (entities.length === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{title}</Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setShowSelector(!showSelector)}
        >
          {showSelector ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              閉じる
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              開く ({selectedIds.length}/{entities.length}{unitName}選択中)
            </>
          )}
        </Button>
      </div>
      
      {showSelector && (
        <>
          {entities.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
          
          <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
            {filteredEntities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-2">
                該当する項目が見つかりません
              </p>
            ) : (
              filteredEntities.map(entity => (
                <div key={entity.id} className="flex items-start gap-2">
                  <Checkbox
                    id={`entity-${entity.id}`}
                    checked={selectedIds.includes(entity.id)}
                    onCheckedChange={() => onToggle(entity.id)}
                  />
                  <Label 
                    htmlFor={`entity-${entity.id}`} 
                    className="flex-1 cursor-pointer text-sm"
                  >
                    <div className="font-medium">{entity.name}</div>
                    {entity.summary && (
                      <div className="text-xs text-muted-foreground">
                        {entity.summary.slice(0, 50)}...
                      </div>
                    )}
                  </Label>
                  {onViewDetail && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onViewDetail(entity)
                      }}
                      title="詳細を表示"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
          
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconComponent className="h-4 w-4" />
              <span>{selectedIds.length}{unitName}を選択中</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}